import { Minus, Plus, RotateCcw } from "lucide-react-native";
import {
  ComponentRef,
  FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { View } from "react-native";
import { ComposableMap, Geographies, Geography } from "react-simple-maps";

import { Box, Pressable, Spinner, Text } from "@/components/ui";
import type { GeographicMapConfig } from "@/types/resources";

import { CONTENT_HEIGHT, DRAG_SENSITIVITY } from "../constants";

function subnationalGeographyStyle(
  isSelected: boolean,
  disabled: boolean,
  strokeWidth: number
) {
  return {
    default: {
      cursor: disabled ? "default" : "pointer",
      fill: isSelected
        ? "rgb(var(--color-primary-500))"
        : "rgb(var(--color-primary-200))",
      outline: "none",
      stroke: "#fff",
      strokeWidth,
    },
    hover: {
      fill: isSelected
        ? "rgb(var(--color-primary-600))"
        : "rgb(var(--color-primary-300))",
      outline: "none",
      stroke: "#fff",
      strokeWidth,
    },
    pressed: {
      fill: "rgb(var(--color-primary-300))",
      outline: "none",
      stroke: "#fff",
      strokeWidth,
    },
  } as const;
}

export interface CountryMapProps {
  disabled: boolean;
  mapConfig: GeographicMapConfig;
  onToggleState: (code: string) => void;
  stateValues: string[];
}

interface GeoFeature {
  [key: string]: unknown;
  id: string;
  properties: Record<string, string>;
  rsmKey: string;
}

const STROKE_WIDTH = 0.75;

/* Size the map by width-driven aspect ratio (matching the 800 × CONTENT_HEIGHT
   frame) rather than a fixed pixel height, so it stays proportionally the same
   on every screen instead of over-tall on mobile. */
const MAP_ASPECT = 800 / CONTENT_HEIGHT;

const resolveProperty = (obj: GeoFeature, path: string): string | undefined => {
  const parts = path.split(".");
  let cur: GeoFeature | Record<string, unknown> = obj;
  for (const p of parts) {
    const next = (cur as Record<string, unknown>)[p];
    if (next == null || typeof next !== "object") {
      return typeof next === "string" ? next : undefined;
    }
    cur = next as Record<string, unknown>;
  }
  return undefined;
};

export const CountryMap: FC<CountryMapProps> = ({
  disabled,
  mapConfig,
  onToggleState,
  stateValues,
}) => {
  const baseScale = mapConfig.scale;
  const baseCenterLng = mapConfig.center[0];
  const baseCenterLat = mapConfig.center[1];

  const baseCenter = useMemo<[number, number]>(
    () => [baseCenterLng, baseCenterLat],
    [baseCenterLng, baseCenterLat]
  );

  const [mapScale, setMapScale] = useState(baseScale);
  const [mapCenter, setMapCenter] = useState<[number, number]>(baseCenter);
  const [tooltip, setTooltip] = useState<{
    label: string;
    x: number;
    y: number;
  } | null>(null);
  const [geoLoaded, setGeoLoaded] = useState(false);

  const dragging = useRef(false);
  // Mirror the drag state for the rendered cursor (a ref can't be read during
  // render). The ref drives the mousemove hot path; the state drives the style.
  const [isDragging, setIsDragging] = useState(false);
  const dragStart = useRef({ x: 0, y: 0 });
  const centerAtDragStart = useRef<[number, number]>(baseCenter);
  const didDrag = useRef(false);
  const wheelRef = useRef<ComponentRef<typeof View>>(null);

  // Reset the view to the base scale/center during render (not in an effect)
  // when the base config changes; the initial values are already seeded above.
  const [prevBase, setPrevBase] = useState({
    center: baseCenter,
    scale: baseScale,
  });
  if (prevBase.scale !== baseScale || prevBase.center !== baseCenter) {
    setPrevBase({ center: baseCenter, scale: baseScale });
    setMapScale(baseScale);
    setMapCenter(baseCenter);
  }

  useEffect(() => {
    const el = wheelRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setMapScale((s) =>
        Math.max(
          baseScale,
          Math.min(e.deltaY < 0 ? s * 1.15 : s / 1.15, baseScale * 8)
        )
      );
    };
    // @ts-expect-error web-only wheel event
    el.addEventListener("wheel", onWheel, { passive: false });
    // @ts-expect-error web-only wheel event
    return () => el.removeEventListener("wheel", onWheel);
  }, [baseScale]);

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      dragging.current = true;
      setIsDragging(true);
      didDrag.current = false;
      dragStart.current = { x: e.clientX, y: e.clientY };
      centerAtDragStart.current = mapCenter;
    },
    [mapCenter]
  );

  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => {
      if (!dragging.current) return;
      const dx = e.clientX - dragStart.current.x;
      const dy = e.clientY - dragStart.current.y;
      if (Math.abs(dx) > 3 || Math.abs(dy) > 3) didDrag.current = true;
      const factor = (DRAG_SENSITIVITY / mapScale) * 300;
      setMapCenter([
        centerAtDragStart.current[0] - dx * factor,
        centerAtDragStart.current[1] + dy * factor,
      ]);
    },
    [mapScale]
  );

  const handleMouseUp = useCallback(() => {
    dragging.current = false;
    setIsDragging(false);
  }, []);

  const handleZoomIn = useCallback(() => {
    setMapScale((s) => Math.min(s * 1.15, baseScale * 8));
  }, [baseScale]);

  const handleZoomOut = useCallback(() => {
    setMapScale((s) => Math.max(s / 1.15, baseScale));
  }, [baseScale]);

  const handleReset = useCallback(() => {
    setMapScale(baseScale);
    setMapCenter(baseCenter);
  }, [baseScale, baseCenter]);

  const zoomPct = Math.round((mapScale / baseScale) * 100);

  const { filter_property, filter_value, id_property } = mapConfig;
  const insets = mapConfig.insets ?? [];

  const parseGeo = useCallback(
    (data: GeoFeature[]) => {
      if (!filter_property || !filter_value) return data;
      return data.filter(
        (g) => resolveProperty(g, filter_property) === filter_value
      );
    },
    [filter_property, filter_value]
  );

  return (
    <Box ref={wheelRef} style={{ position: "relative" }}>
      <Box
        onMouseDown={handleMouseDown}
        onMouseLeave={handleMouseUp}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        // @ts-expect-error web-only CSS
        style={{
          aspectRatio: MAP_ASPECT,
          backgroundColor: "rgb(var(--color-background-0))",
          cursor: disabled ? "default" : isDragging ? "grabbing" : "grab",
          overflow: "hidden",
          position: "relative",
          userSelect: "none",
          width: "100%",
        }}
      >
        {!geoLoaded && (
          <Box
            style={{
              alignItems: "center",
              height: "100%",
              justifyContent: "center",
              left: 0,
              position: "absolute",
              top: 0,
              width: "100%",
              zIndex: 1,
            }}
          >
            <Spinner color="rgb(var(--color-primary-500))" size="small" />
          </Box>
        )}
        <ComposableMap
          projection={mapConfig.projection}
          projectionConfig={{
            center: mapCenter,
            scale: mapScale,
          }}
          style={{
            display: "block",
            height: "100%",
            width: "100%",
          }}
        >
          {/* @ts-expect-error react-simple-maps Feature type mismatch */}
          <Geographies geography={mapConfig.url} parseGeographies={parseGeo}>
            {({ geographies }: { geographies: GeoFeature[] }) => {
              if (!geoLoaded) setTimeout(() => setGeoLoaded(true), 0);
              return geographies
                .map((geo) => {
                  const stateId = resolveProperty(geo, id_property);
                  if (!stateId) return null;
                  const isSelected = stateValues.includes(stateId);
                  const label =
                    geo.properties.NAME_LATN ?? geo.properties.name ?? stateId;
                  return (
                    <Geography
                      geography={geo}
                      key={geo.rsmKey}
                      onClick={() => {
                        if (!didDrag.current) onToggleState(stateId);
                      }}
                      onMouseEnter={(e) =>
                        setTooltip({ label, x: e.clientX, y: e.clientY })
                      }
                      onMouseLeave={() => setTooltip(null)}
                      onMouseMove={(e) =>
                        setTooltip({ label, x: e.clientX, y: e.clientY })
                      }
                      style={subnationalGeographyStyle(
                        isSelected,
                        disabled,
                        STROKE_WIDTH
                      )}
                    />
                  );
                })
                .filter(Boolean);
            }}
          </Geographies>
        </ComposableMap>

        {insets.length > 0 && (
          <Box
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 4,
              left: 8,
              pointerEvents: "auto" as const,
              position: "absolute",
              top: 8,
            }}
          >
            {insets.map((inset) => {
              const isInsetSelected = stateValues.includes(inset.state_value);
              return (
                <Box
                  key={inset.state_value}
                  style={{
                    height: 58,
                    overflow: "hidden",
                    position: "relative",
                    width: 80,
                  }}
                >
                  <ComposableMap
                    height={58}
                    projection="geoMercator"
                    projectionConfig={{
                      center: inset.center,
                      scale: inset.scale,
                    }}
                    width={80}
                  >
                    <Geographies
                      geography={mapConfig.url}
                      // @ts-expect-error react-simple-maps Feature type mismatch
                      parseGeographies={(data: GeoFeature[]) =>
                        data.filter(
                          (g) =>
                            resolveProperty(g, id_property) ===
                            inset.state_value
                        )
                      }
                    >
                      {({ geographies }: { geographies: GeoFeature[] }) =>
                        geographies.map((geo) => (
                          <Geography
                            geography={geo}
                            key={geo.rsmKey}
                            onClick={() => onToggleState(inset.state_value)}
                            onMouseEnter={(e) =>
                              setTooltip({
                                label: inset.label,
                                x: e.clientX,
                                y: e.clientY,
                              })
                            }
                            onMouseLeave={() => setTooltip(null)}
                            onMouseMove={(e) =>
                              setTooltip({
                                label: inset.label,
                                x: e.clientX,
                                y: e.clientY,
                              })
                            }
                            style={subnationalGeographyStyle(
                              isInsetSelected,
                              disabled,
                              STROKE_WIDTH
                            )}
                          />
                        ))
                      }
                    </Geographies>
                  </ComposableMap>
                </Box>
              );
            })}
          </Box>
        )}
      </Box>

      <Box
        style={{
          bottom: 12,
          display: "flex",
          flexDirection: "column",
          gap: 4,
          position: "absolute",
          right: 12,
        }}
      >
        <Pressable onPress={handleZoomIn}>
          <Box className="w-7 h-7 rounded-md border border-outline-200 bg-white items-center justify-center">
            <Plus color="rgb(var(--color-typography-600))" size={14} />
          </Box>
        </Pressable>
        <Box className="h-7 items-center justify-center">
          <Text className="text-xs text-typography-500 font-mono">
            {zoomPct}%
          </Text>
        </Box>
        <Pressable onPress={handleZoomOut}>
          <Box className="w-7 h-7 rounded-md border border-outline-200 bg-white items-center justify-center">
            <Minus color="rgb(var(--color-typography-600))" size={14} />
          </Box>
        </Pressable>
        <Pressable onPress={handleReset}>
          <Box className="w-7 h-7 rounded-md border border-outline-200 bg-white items-center justify-center">
            <RotateCcw color="rgb(var(--color-typography-600))" size={12} />
          </Box>
        </Pressable>
      </Box>

      {tooltip &&
        createPortal(
          <div
            style={{
              background: "rgb(var(--color-background-800))",
              borderRadius: 6,
              color: "rgb(var(--color-typography-0))",
              fontSize: 12,
              left: tooltip.x + 12,
              padding: "4px 8px",
              pointerEvents: "none",
              position: "fixed",
              top: tooltip.y + 12,
              whiteSpace: "nowrap",
              zIndex: 9999,
            }}
          >
            {tooltip.label}
          </div>,
          document.body
        )}
    </Box>
  );
};
